import queryString from 'query-string'
import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'
import type {
  IThuocTinh,
  IThuocTinhPayload,
  IThuocTinhGiaTri,
  IThuocTinhGiaTriPayload,
  IThuocTinhDropdownItem,
  TrangThaiThuocTinh,
  LoaiInputThuocTinh,
} from '@/interfaces/thuocTinhInterfaces'

const PREFIX = '/thuoc-tinh'

function mapThuocTinhFromApi(raw: Record<string, unknown>): IThuocTinh {
  const trangThai = raw.trangThai
  const loaiInput = raw.loaiInput
  const rawNguoiTao = raw.nguoiTao as { id?: number; username?: string } | null | undefined
  const rawNguoiCapNhat = raw.nguoiCapNhat as { id?: number; username?: string } | null | undefined
  const rawCount = raw._count as { giaTris?: number; hangHoaGan?: number } | null | undefined
  return {
    id: String(raw.id ?? ''),
    maThuocTinh: String(raw.maThuocTinh ?? ''),
    ten: String(raw.ten ?? ''),
    moTa: raw.moTa != null ? String(raw.moTa) : null,
    loaiInput: loaiInput === 'text' || loaiInput === 'select' || loaiInput === 'number' ? (loaiInput as LoaiInputThuocTinh) : 'text',
    thuTu: typeof raw.thuTu === 'number' ? raw.thuTu : 0,
    trangThai: trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong' ? (trangThai as TrangThaiThuocTinh) : 'dangHoatDong',
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
    nguoiTao: rawNguoiTao?.username ? { id: Number(rawNguoiTao.id ?? 0), username: rawNguoiTao.username } : null,
    nguoiCapNhat: rawNguoiCapNhat?.username ? { id: Number(rawNguoiCapNhat.id ?? 0), username: rawNguoiCapNhat.username } : null,
    giaTris: Array.isArray(raw.giaTris)
      ? (raw.giaTris as Record<string, unknown>[]).map((g) => ({
          id: String(g.id ?? ''),
          thuocTinhId: String(g.thuocTinhId ?? ''),
          giaTri: String(g.giaTri ?? ''),
          thuTu: typeof g.thuTu === 'number' ? g.thuTu : 0,
          trangThai: g.trangThai === 'dangHoatDong' || g.trangThai === 'ngungHoatDong' ? (g.trangThai as TrangThaiThuocTinh) : 'dangHoatDong',
        }))
      : undefined,
    _count: rawCount ?? undefined,
  }
}

function mapDropdownFromApi(raw: Record<string, unknown>): IThuocTinhDropdownItem {
  const loaiInput = raw.loaiInput
  return {
    id: String(raw.id ?? ''),
    ten: String(raw.ten ?? ''),
    maThuocTinh: String(raw.maThuocTinh ?? ''),
    loaiInput: loaiInput === 'text' || loaiInput === 'select' || loaiInput === 'number' ? (loaiInput as LoaiInputThuocTinh) : 'text',
    giaTris: Array.isArray(raw.giaTris)
      ? (raw.giaTris as Record<string, unknown>[]).map((g) => ({ id: String(g.id ?? ''), giaTri: String(g.giaTri ?? '') }))
      : [],
  }
}

function toPayload(body: IThuocTinhPayload) {
  const payload: Record<string, unknown> = { ten: body.ten.trim() }
  if (body.maThuocTinh?.trim()) payload.maThuocTinh = body.maThuocTinh.trim()
  if (body.moTa?.trim()) payload.moTa = body.moTa.trim()
  if (body.loaiInput) payload.loaiInput = body.loaiInput
  if (typeof body.thuTu === 'number') payload.thuTu = body.thuTu
  if (body.danhSachGiaTri?.length) payload.danhSachGiaTri = body.danhSachGiaTri
  return payload
}

export function thuocTinhListQueryFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = {}
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  if (typeof q.search === 'string' && q.search.trim()) out.search = q.search.trim()
  if (typeof q.ten === 'string' && q.ten.trim()) out.ten = q.ten.trim()
  if (q.loaiInput === 'text' || q.loaiInput === 'select' || q.loaiInput === 'number') out.loaiInput = q.loaiInput
  if (q.trangThai === 'dangHoatDong' || q.trangThai === 'ngungHoatDong') out.trangThai = q.trangThai
  if (typeof q.sort === 'string' && q.sort.trim()) out.sort = q.sort.trim()
  if (q.sortDir === 'asc' || q.sortDir === 'desc') out.sortDir = q.sortDir
  return out
}

// ===== Thuộc tính (master) =====

export function fetchDanhSachThuocTinh(search: string): Promise<PagedListResult<IThuocTinh>> {
  const params = thuocTinhListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-thuoc-tinh`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: (data as Record<string, unknown>[]).map(mapThuocTinhFromApi),
        paging,
      }
    }),
  )
}

export function fetchChiTietThuocTinh(id: string | number): Promise<IThuocTinh> {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-thuoc-tinh`).then((res: unknown) =>
      mapThuocTinhFromApi(normalizeDetailResponse(res) as Record<string, unknown>),
    ),
  )
}

export function taoThuocTinh(body: IThuocTinhPayload): Promise<{ data: IThuocTinh }> {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/tao-thuoc-tinh`, toPayload(body))).then((res: unknown) => {
    const wrapped = res as { data?: Record<string, unknown> } | Record<string, unknown>
    const raw = (wrapped as { data?: Record<string, unknown> }).data ?? (wrapped as Record<string, unknown>)
    return { data: mapThuocTinhFromApi(raw) }
  })
}

export function capNhatThuocTinh(id: string | number, body: IThuocTinhPayload) {
  return apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-thuoc-tinh`, toPayload(body)))
}

export function xoaThuocTinh(id: string | number) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-thuoc-tinh`))
}

export type XoaNhieuThuocTinhResult = {
  ok: number
  failed: Array<{ id: string; message: string }>
}

/** Xóa nhiều thuộc tính — gọi API xóa từng id (loại trùng id). */
export async function xoaNhieuThuocTinh(ids: string[]): Promise<XoaNhieuThuocTinhResult> {
  const uniqueIds = Array.from(new Set(ids.map((id) => String(id).trim()).filter(Boolean)))
  const failed: XoaNhieuThuocTinhResult['failed'] = []
  let ok = 0
  for (const id of uniqueIds) {
    try {
      await xoaThuocTinh(id)
      ok += 1
    } catch (e) {
      failed.push({ id, message: (e as { message?: string })?.message ?? '' })
    }
  }
  return { ok, failed }
}

export function chuyenTrangThaiThuocTinh(id: string | number, trangThai: TrangThaiThuocTinh) {
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-thuoc-tinh`, { trangThai }))
}

/** Lấy tất cả thuộc tính + giá trị để dùng cho dropdown trong ProductForm. */
export function fetchThuocTinhDropdown(): Promise<IThuocTinhDropdownItem[]> {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/dropdown-thuoc-tinh`).then((res: unknown) => {
      const raw = normalizeDetailResponse(res)
      return Array.isArray(raw) ? (raw as Record<string, unknown>[]).map(mapDropdownFromApi) : []
    }),
  )
}

// ===== Giá trị thuộc tính (nested) =====

export function fetchDanhSachGiaTri(thuocTinhId: string): Promise<IThuocTinhGiaTri[]> {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${thuocTinhId}/gia-tri`).then((res: unknown) => {
      const raw = normalizeDetailResponse(res)
      return Array.isArray(raw)
        ? (raw as Record<string, unknown>[]).map((g) => ({
            id: String(g.id ?? ''),
            thuocTinhId: String(g.thuocTinhId ?? thuocTinhId),
            giaTri: String(g.giaTri ?? ''),
            thuTu: typeof g.thuTu === 'number' ? g.thuTu : 0,
            trangThai: g.trangThai === 'dangHoatDong' || g.trangThai === 'ngungHoatDong' ? (g.trangThai as TrangThaiThuocTinh) : 'dangHoatDong',
          }))
        : []
    }),
  )
}

export function taoGiaTriThuocTinh(thuocTinhId: string, body: IThuocTinhGiaTriPayload): Promise<{ data: IThuocTinhGiaTri }> {
  return apiRequest(
    ApisServiceApp.post(`${PREFIX}/${thuocTinhId}/gia-tri`, {
      giaTri: body.giaTri.trim(),
      ...(typeof body.thuTu === 'number' ? { thuTu: body.thuTu } : {}),
    }),
  ).then((res: unknown) => {
    const wrapped = res as { data?: Record<string, unknown> } | Record<string, unknown>
    const raw = (wrapped as { data?: Record<string, unknown> }).data ?? (wrapped as Record<string, unknown>)
    return {
      data: {
        id: String(raw.id ?? ''),
        thuocTinhId: String(raw.thuocTinhId ?? thuocTinhId),
        giaTri: String(raw.giaTri ?? ''),
        thuTu: typeof raw.thuTu === 'number' ? raw.thuTu : 0,
        trangThai: raw.trangThai === 'dangHoatDong' || raw.trangThai === 'ngungHoatDong' ? (raw.trangThai as TrangThaiThuocTinh) : 'dangHoatDong',
      },
    }
  })
}

export function capNhatGiaTriThuocTinh(thuocTinhId: string, giaTriId: string, body: IThuocTinhGiaTriPayload) {
  return apiRequest(
    ApisServiceApp.put(`${PREFIX}/${thuocTinhId}/gia-tri/${giaTriId}/cap-nhat-gia-tri-thuoc-tinh`, {
      giaTri: body.giaTri.trim(),
      ...(typeof body.thuTu === 'number' ? { thuTu: body.thuTu } : {}),
    }),
  )
}

export function xoaGiaTriThuocTinh(thuocTinhId: string, giaTriId: string) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${thuocTinhId}/gia-tri/${giaTriId}/xoa-gia-tri-thuoc-tinh`))
}
